import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUpDown, Menu, Pencil, Plus, Search, AlertTriangle, Users } from "lucide-react";
import { LinkedClientsDialog } from "@/components/LinkedClientsDialog";
import { AffiliateNav } from "@/components/AffiliateNav";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "@/lib/authFetch";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BH_OFFICES } from "@/constants/locations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const TYPES = { preferred_lender: "Preferred Lender", sales_rep: "Betenbough Sales Rep", referral_partner: "Realtor / Referral Partner", affiliate: "Affiliate" };
const TYPE_STYLE = { preferred_lender: "bg-emerald-100 text-emerald-800", sales_rep: "bg-sky-100 text-sky-800", referral_partner: "bg-amber-100 text-amber-800", affiliate: "bg-slate-200 text-slate-700" };
const FIELD_LABEL = { lender: "Affiliated Lender", affiliate_1: "Affiliate 1", affiliate_2: "Affiliate 2" };
const EMPTY = { name: "", email: "", company: "", phone: "", offices: [], show_on_forms: true, affiliate_type: "referral_partner" };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const post = (url, method, body) => authFetch(url, { method, headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });

// Promise-based confirm dialog: const ok = await confirm({ title, body })
const useConfirm = () => {
  const [state, setState] = useState(null);
  const resolver = useRef(null);
  const confirm = (opts) => new Promise((res) => { resolver.current = res; setState(opts); });
  const close = (v) => { resolver.current?.(v); setState(null); };
  const dialog = (
    <AlertDialog open={!!state} onOpenChange={(o) => !o && close(false)}>
      <AlertDialogContent data-testid="affiliate-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{state?.title}</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="space-y-2 text-sm">{state?.body}</div></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)} data-testid="affiliate-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => close(true)} data-testid="affiliate-confirm-continue">Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return [confirm, dialog];
};

const ImpactBody = ({ imp, oldName, newName }) => (
  <>
    <p><b>{imp.total}</b> existing client{imp.total === 1 ? "" : "s"} list <b>{oldName}</b>. They'll be updated to <b>{newName}</b> in the CRM and the portal:</p>
    <ul className="list-disc pl-5">
      {Object.entries(imp.counts).filter(([, n]) => n).map(([k, n]) => <li key={k}>{FIELD_LABEL[k]}: {n}</li>)}
    </ul>
    {imp.unmatched?.length > 0 && (
      <p className="text-amber-700">Not matched (spelled differently, won't change): {imp.unmatched.slice(0, 5).map((u) => `${u.name} ("${u.listed_as}")`).join(", ")}{imp.unmatched.length > 5 ? ` and ${imp.unmatched.length - 5} more` : ""}</p>
    )}
  </>
);

const OfficePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="affiliate-office-picker">
    {BH_OFFICES.map((o) => (
      <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={value.includes(o)} onCheckedChange={(c) => onChange(c ? [...value, o] : value.filter((x) => x !== o))} data-testid={`affiliate-office-${slug(o)}`} />
        {o}
      </label>
    ))}
  </div>
);

const pollJob = async (jobId, total) => {
  const id = toast.loading(`Updating ${total} existing client${total === 1 ? "" : "s"}…`);
  for (let i = 0; i < 200; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    const job = await (await authFetch(`${API}/affiliate-jobs/${jobId}`, { credentials: "include" })).json();
    if (job.status === "done") {
      toast.dismiss(id);
      if (job.failed?.length) toast.error(`${job.updated} clients updated, ${job.failed.length} failed: ${job.failed.slice(0, 5).map((f) => f.name).join(", ")}`, { duration: 15000 });
      else toast.success(`${job.updated} existing clients updated`);
      return;
    }
  }
  toast.dismiss(id);
};

const AffiliateDialog = ({ affiliate, onClose, onSaved, confirm, onLinked, linkedCount }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isNew = affiliate && !affiliate.user_id;
  const isLender = isNew ? form.affiliate_type === "preferred_lender" : affiliate?.is_lender;
  useEffect(() => { if (affiliate) setForm({ ...EMPTY, ...affiliate }); }, [affiliate]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, reactivate: false, confirm_client_updates: false };
      if (!isNew) {
        if (isLender && form.show_on_forms && !affiliate.is_active) {
          const ok = await confirm({ title: "Reactivate account?", body: "This lender's portal account is inactive. Showing them on the referral forms will also reactivate their account so they can log in. Continue?" });
          if (!ok) return;
          body.reactivate = true;
        }
        const imp = await (await post(`${API}/affiliates/${affiliate.user_id}/impact`, "POST", body)).json();
        if (imp.total > 0) {
          const ok = await confirm({ title: "Update existing clients?", body: <ImpactBody imp={imp} oldName={imp.old_name} newName={form.name.trim()} /> });
          if (!ok) return;
          body.confirm_client_updates = true;
        }
      }
      const res = await post(isNew ? `${API}/affiliates` : `${API}/affiliates/${affiliate.user_id}`, isNew ? "POST" : "PUT", body);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Please check the name and email");
      toast.success(isNew ? "Affiliate added — CRM contact being created" : "Saved — CRM contact updating");
      onSaved();
      if (data.job_id) pollJob(data.job_id, data.clients_to_update);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!affiliate} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="affiliate-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add Affiliate" : "Edit Affiliate"}</DialogTitle>
          <DialogDescription>Saves to the portal and the affiliate's CRM contact. The email is also their portal login.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isNew ? (
            <div>
              <Label>Type</Label>
              <Select value={form.affiliate_type} onValueChange={(v) => setForm((f) => ({ ...f, affiliate_type: v }))}>
                <SelectTrigger className="mt-1" data-testid="affiliate-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">{affiliate?.types?.map((t) => <Badge key={t} className={TYPE_STYLE[t]}>{TYPES[t]}</Badge>)}</div>
          )}
          {[["name", "Name"], ["email", "Email"], ["company", "Company"], ["phone", "Phone"]].map(([k, label]) => (
            <div key={k}>
              <Label htmlFor={`affiliate-${k}`}>{label}</Label>
              <Input id={`affiliate-${k}`} value={form[k] || ""} onChange={set(k)} className="mt-1" data-testid={`affiliate-${k}-input`} />
            </div>
          ))}
          {isLender && (
            <>
              <div>
                <Label>Betenbough offices</Label>
                <p className="text-xs text-slate-500 mb-2">Shown at the top of the lender list when a partner picks one of these offices.</p>
                <OfficePicker value={form.offices} onChange={(offices) => setForm((f) => ({ ...f, offices }))} />
              </div>
              <label className="flex items-center justify-between rounded-lg border p-3 cursor-pointer">
                <span className="text-sm">Show on referral forms</span>
                <Switch checked={form.show_on_forms} onCheckedChange={(v) => setForm((f) => ({ ...f, show_on_forms: v }))} data-testid="affiliate-show-switch" />
              </label>
            </>
          )}
        </div>
        <DialogFooter className="sm:justify-between">
          {!isNew ? <Button variant="secondary" onClick={() => onLinked(affiliate)} data-testid="affiliate-linked-clients-btn"><Users className="h-4 w-4 mr-2" />Linked Clients{linkedCount !== undefined ? ` (${linkedCount})` : ""}</Button> : <span />}
          <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} data-testid="affiliate-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name.trim() || !form.email.trim()} data-testid="affiliate-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AffiliateRow = ({ a, onEdit, onLinked, count }) => (
  <TableRow data-testid={`affiliate-row-${a.user_id}`} className={a.is_active ? "" : "opacity-60"}>
    <TableCell className="font-medium">{a.name}</TableCell>
    <TableCell><Badge className={TYPE_STYLE[a.type]} data-testid={`affiliate-type-${a.user_id}`}>{TYPES[a.type]}</Badge></TableCell>
    <TableCell>{a.company || <span className="text-amber-600 text-xs">No company</span>}</TableCell>
    <TableCell className="text-sm">{a.email}</TableCell>
    <TableCell>
      {a.is_lender ? (
        <div className="flex flex-wrap gap-1">{a.offices.length ? a.offices.map((o) => <Badge key={o} variant="secondary">{o}</Badge>) : <span className="text-amber-600 text-xs">No office</span>}</div>
      ) : <span className="text-slate-400 text-xs">—</span>}
    </TableCell>
    <TableCell>
      <div className="flex flex-wrap gap-1">
        {a.is_lender && (a.show_on_forms ? <Badge>Shown</Badge> : <Badge variant="outline">Hidden</Badge>)}
        {a.is_active ? <Badge variant="secondary">Active</Badge> : <Badge variant="destructive">Inactive</Badge>}
      </div>
    </TableCell>
    <TableCell>
      {count === undefined ? <span className="text-slate-400 text-xs">…</span> : (
        <button onClick={() => onLinked(a)} className="font-medium text-[#357c2a] hover:underline tabular-nums" data-testid={`affiliate-linked-count-${a.user_id}`}>{count}</button>
      )}
    </TableCell>
    <TableCell className="text-right whitespace-nowrap">
      <Button size="sm" variant="ghost" onClick={() => onLinked(a)} aria-label={`Linked clients for ${a.name}`} title="Linked clients" data-testid={`affiliate-linked-btn-${a.user_id}`}><Users className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" onClick={() => onEdit(a)} aria-label={`Edit ${a.name}`} title="Edit affiliate" data-testid={`affiliate-edit-btn-${a.user_id}`}><Pencil className="h-4 w-4" /></Button>
    </TableCell>
  </TableRow>
);

const cmp = (x, y) => x.localeCompare(y, undefined, { sensitivity: "base" });
const SORTS = {
  name: (x, y) => cmp(x.name, y.name),
  company: (x, y) => (!x.company - !y.company) || cmp(x.company, y.company) || cmp(x.name, y.name),
  type: (x, y) => cmp(TYPES[x.type], TYPES[y.type]) || cmp(x.name, y.name),
  updated: (x, y) => (y.updated_at || "").localeCompare(x.updated_at || ""),
  linked: (x, y) => (y.linked || 0) - (x.linked || 0) || cmp(x.name, y.name),
};

export default function AffiliateManagement() {
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState({ affiliates: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [type, setType] = useState("all");
  const [attention, setAttention] = useState(false);
  const [sort, setSort] = useState("name");
  const [editing, setEditing] = useState(null);
  const [linked, setLinked] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const [counts, setCounts] = useState(null);
  const loadCounts = (refresh) => authFetch(`${API}/linked-counts${refresh ? "?refresh=true" : ""}`, { credentials: "include" })
    .then((r) => r.json()).then((d) => setCounts(d.counts || {})).catch(() => {});
  useEffect(() => {
    loadCounts();
    const h = () => loadCounts(true);
    window.addEventListener("link-job-done", h);
    return () => window.removeEventListener("link-job-done", h);
  }, []);
  const load = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/affiliates`, { credentials: "include" });
    if (res.ok) setData(await res.json());
    else toast.error("Could not load affiliates");
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    const q = query.toLowerCase();
    const rows = data.affiliates.filter((a) => (type === "all" || a.types.includes(type)) && (!attention || a.needs_attention)
      && [a.name, a.email, a.company].join(" ").toLowerCase().includes(q)).map((a) => ({ ...a, linked: counts?.[a.user_id] || 0 }));
    return [...rows].sort(SORTS[sort]);
  }, [data, query, type, attention, sort, counts]);

  const chips = [["all", "All", data.affiliates.length], ...Object.entries(TYPES).map(([k, l]) => [k, `${l}s`, data.counts[k] || 0])];
  const attentionCount = data.affiliates.filter((a) => (type === "all" || a.types.includes(type)) && a.needs_attention).length;

  return (
    <div className={`flex min-h-screen ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={true} />
      <main className="flex-1 min-w-0 md:ml-64">
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 border-b ${isDarkMode ? "bg-black border-[#1a3d18]" : "header-glass"}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-slate-100"><Menu className="h-5 w-5" /></button>
              <div>
                <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`} data-testid="affiliate-page-title">Affiliate Management</h1>
                <p className="text-sm text-slate-500" data-testid="lender-page-subtitle">Manage existing or add new affiliates. Affiliates selected as "Shown" are populated as options in the add client forms. Changes and additions also update the CRM.</p>
              </div>
            </div>
            <Button onClick={() => setEditing({ ...EMPTY })} data-testid="add-affiliate-btn"><Plus className="h-4 w-4 mr-2" />Add Affiliate</Button>
          </div>
          <AffiliateNav />
        </header>
        <div className="p-4 md:p-6 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="affiliate-type-chips">
            {chips.map(([k, l, n]) => (
              <button key={k} onClick={() => setType(k)} data-testid={`affiliate-chip-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${type === k ? "bg-[#357c2a] text-white border-[#357c2a]" : "bg-white text-slate-700 hover:bg-slate-100"}`}>
                {l} ({n})
              </button>
            ))}
            <button onClick={() => setAttention((v) => !v)} data-testid="affiliate-chip-attention"
              className={`px-3 py-1.5 rounded-full text-sm border flex items-center gap-1 transition-colors ${attention ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 hover:bg-amber-50"}`}>
              <AlertTriangle className="h-3.5 w-3.5" />Needs attention ({attentionCount})
            </button>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative w-full max-w-sm">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, company, email" className="pl-9" data-testid="affiliate-search-input" />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48" data-testid="affiliate-sort-select"><ArrowUpDown className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Sort: Name</SelectItem><SelectItem value="company">Sort: Company</SelectItem>
                <SelectItem value="type">Sort: Type</SelectItem><SelectItem value="updated">Sort: Last updated</SelectItem>
                <SelectItem value="linked">Sort: Linked clients</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className={`rounded-xl border ${isDarkMode ? "bg-[#0d1f0c] border-[#1a3d18] text-white" : "bg-white"}`}>
            <Table data-testid="affiliate-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Company</TableHead><TableHead>Email</TableHead>
                  <TableHead>Offices</TableHead><TableHead>Status</TableHead>
                  <TableHead><button onClick={() => setSort("linked")} className="hover:underline whitespace-nowrap" data-testid="affiliate-linked-header">Linked clients</button></TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
                ) : shown.map((a) => <AffiliateRow key={a.user_id} a={a} onEdit={setEditing} onLinked={setLinked} count={counts ? counts[a.user_id] || 0 : undefined} />)}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-slate-500" data-testid="affiliate-count">{shown.length} of {data.affiliates.length} affiliates</p>
        </div>
      </main>
      <AffiliateDialog affiliate={editing} confirm={confirm} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onLinked={setLinked}
        linkedCount={counts && editing?.user_id ? counts[editing.user_id] || 0 : undefined} />
      <LinkedClientsDialog person={linked} onClose={() => setLinked(null)} />
      {confirmDialog}
    </div>
  );
}
